import { type GraphSeriesOption } from 'echarts'
import { WaitingScope } from 'src/components/waiting/waiting.scopes'
import { IDialogScope } from 'src/main'
import { v4 as uuid } from 'uuid'
import { events, NOOP_VOID, Observable, ObservableArray, observe, Scope } from 'wdc-cube'

import { type EChartsOption } from 'src/components/echarts'
import { type GraphNode } from './tc_types'

import { type SearchOption, SearchBoxScope } from 'src/components/searchbox'
import { ConfirmExportDataLimitScope } from 'src/main/Main.scopes'
import { TableEmpresaScope, TableQSAScope } from '../the-consumer-market/tcm_scopes'

type BaseEvent = events.BaseEvent
type KeyPressEvent = events.KeyPressEvent

export { EChartsOption, WaitingScope, TableEmpresaScope, TableQSAScope }
export type { BaseEvent, KeyPressEvent }

export type TcListagemTabsOption = 'pj' | 'qsa'

@Observable
export class SearchInputScope extends Scope {
    readonly uid = uuid()

    @observe() compliance = false
    @observe() label?: string
    @observe() placeholder?: string = 'Busca'
    @observe() loading = false
    @observe() value = ''
    @observe() selecteds: SearchOption[] = []
    @observe() options: SearchOption[] = []
    @observe() filterId = '_'

    focus: (cb?: () => void) => void = NOOP_VOID

    @observe() onInputChange = Scope.SYNC_ACTION_TWO<BaseEvent, string>()
    @observe() onChange = Scope.SYNC_ACTION_THREE<BaseEvent, SearchOption[], string>()
    @observe() onKeyUp = Scope.SYNC_ACTION_ONE<KeyPressEvent>()
    @observe() onKeyDown = Scope.SYNC_ACTION_ONE<KeyPressEvent>()
    @observe() onFocus = Scope.SYNC_ACTION
    @observe() onBlur = Scope.SYNC_ACTION
    @observe() onRemoveTag = Scope.ASYNC_ACTION_ONE<number>()
    @observe() onTextSearch = Scope.ASYNC_ACTION_ONE<string>()
    @observe() onPasteText = Scope.SYNC_ACTION_TWO<string, { accept: boolean }>()
    @observe() onFilterChanged = Scope.ASYNC_ACTION_ONE<string>()
}

@Observable
export class HeaderBarScope extends Scope {
    @observe() onlyActives = true
    @observe() exclusive = true
    @observe() acceptPhotos = false

    readonly search = new SearchBoxScope()

    onOpenSearchByPicture = Scope.ASYNC_ACTION
    onChangeExclusive = Scope.ASYNC_ACTION_BOOLEAN
    onChangeOnlyActives = Scope.ASYNC_ACTION_BOOLEAN
}

@Observable
export class ChipScope extends Scope {
    @observe() label = ''
    @observe() category = 0
    @observe() hint = ''
    @observe() value = ''

    onDelete = Scope.ASYNC_ACTION
}

@Observable
export class SelectedNodesScope extends Scope {
    readonly companies = new ObservableArray<ChipScope>(this)
    readonly partners = new ObservableArray<ChipScope>(this)
    readonly people = new ObservableArray<ChipScope>(this)
}

@Observable
export class SearchByPictureScope extends Scope implements IDialogScope {
    @observe() userFace = false
    @observe() rearCamera: MediaDeviceInfo | undefined
    @observe() frontCamera: MediaDeviceInfo | undefined

    onCapture = Scope.ASYNC_ACTION_ONE<string>()
    onClose = Scope.ASYNC_ACTION
}

@Observable
export class HoverBarScope extends Scope {
    @observe() isEmpresaDownloading = false
    @observe() isSociosDownloading = false
    @observe() isMedicosDownloading = false
    @observe() isVinculosDownloading = false

    @observe() downloadEnabled = true

    @observe() labelEnabled = true
    @observe() allNodesEnabled = true

    @observe() isDisplayingData = false

    @observe() graphExpanded = false
    @observe() expandEnabled = false

    onToggleGraphExpanded = Scope.ASYNC_ACTION
    onZoomIn = Scope.ASYNC_ACTION
    onZoomOut = Scope.ASYNC_ACTION
    onToggleLabels = Scope.ASYNC_ACTION
    onToggleGraphSize = Scope.ASYNC_ACTION
    onDownloadXlsxEmpresas = Scope.ASYNC_ACTION
    onDownloadXlsxSocios = Scope.ASYNC_ACTION
    onDownloadXlsxMedicos = Scope.ASYNC_ACTION
    onDownloadXlsxVinculos = Scope.ASYNC_ACTION
}

@Observable
export class ListagemTabsScope extends Scope {
    @observe() active: TcListagemTabsOption = 'pj'
    @observe() expanded = false

    readonly empresasListagem = new TableEmpresaScope()
    readonly qsaListagem = new TableQSAScope()

    onSelect = Scope.ASYNC_ACTION_ONE<TcListagemTabsOption>()
    onExpandClick = Scope.ASYNC_ACTION
    onShrunkClick = Scope.ASYNC_ACTION
}

@Observable
export class TheConnectionScope extends Scope {
    @observe() hasGraphData = false

    exportDataLimitScope = new ConfirmExportDataLimitScope()

    @observe() waiting?: WaitingScope
    @observe() options?: EChartsOption

    @observe() maximizeCard: 'graph' | 'tables' | '' = ''

    @observe() tablesVisible = false

    @observe() selectedNodesPanel?: SelectedNodesScope

    readonly hoverBar: HoverBarScope = new HoverBarScope()
    readonly listagemTabs: ListagemTabsScope = new ListagemTabsScope()

    scaleZoom: (value: number) => void = NOOP_VOID
    getZoomValue: () => number = DefaultZoomValue
    mergeSerieOptions: (options: GraphSeriesOption) => void = NOOP_VOID
    refreshChart: () => void = NOOP_VOID

    onReady = Scope.ASYNC_ACTION
    onNodeClicked = Scope.ASYNC_ACTION_TWO<GraphNode, boolean>()
    onCopyInformationalData = Scope.ASYNC_ACTION_ONE<GraphNode>()
    onCopyIdentityData = Scope.ASYNC_ACTION_ONE<GraphNode>()
}

function DefaultZoomValue() {
    return 0.6
}
